import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ErrorToast, SuccessToast } from '../../../../utils/toasters';
import { IContentModal } from '../book/offer/dto/IContentModal';
import { ManageCalendar } from './calendar';
import { iFilterInfluencers, iInfluencersData } from './calendar/dto/IManageCalendar';
import { ManageCards } from './cards/ManageCards';
import { ManageService } from './service';
import { useCalendarFilters } from '../../../../shared/hooks/useCalendarFilters';
import { BsExclamationOctagon } from 'react-icons/bs';
import { BiRefresh } from 'react-icons/bi';
import { getCorrectColor, getCorrectFilterTitle } from '../../../../shared/functions/Functions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { useTranslation } from 'react-i18next';
import { scrollToTop } from '../../../../utils/helpers/ScrollToTop';
import { IFilterManageInfluencers } from './cards/dto/IFilterManageInfluencers';
import { selectedCampaignSlice } from '../../../../store/brand/activeCampaignSlice';
import { Row } from 'react-bootstrap';

export const getInfluencersData = (
    campaignId: number,
    filterInfluencers: iFilterInfluencers,
    stateSet: React.Dispatch<SetStateAction<IContentModal[]>>,
    setTotalPages?: React.Dispatch<SetStateAction<number>>,
    setInfluencersInfoData?: React.Dispatch<SetStateAction<iInfluencersData>>
) => {
    ManageService.getInfluencers(campaignId, filterInfluencers)
        .then(response => {
            stateSet(response.data);
            setInfluencersInfoData &&
                setInfluencersInfoData({
                    count: response.count,
                    totalPlanned: response.totalPlanned,
                });
            if (setTotalPages) {
                setTotalPages(Math.ceil(response?.count / response?.perPage));
            }
            // currently we are showing just these 2, if you want to show more, just adjust filter above
        })
        .catch(error => ErrorToast(error));
};

export function CampaignManage() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { offerItemEventsEnum } = useSelector((state: IAllStates) => state.enums);
    const [filterInfluencers, setFilterInfluencers] = useState<IFilterManageInfluencers>({
        page: 1,
        perPage: 15,
        displayName: '',
    });
    const [totalPages, setTotalPages] = useState(0);
    const { id: campaignId }: any = useParams();

    const [influencerDropdownList, setInfluencerDropdownList] = useState<IContentModal[]>([]);
    const [listOfInfluencers, setListOfInfluencers] = useState<IContentModal[]>([]);
    const [influencersInfoData, setInfluencersInfoData] = useState<iInfluencersData>();
    const [triggerUpdate, setTriggerUpdate] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [newEvent, setNewEvent] = useState({});
    const { isAclLoaded } = useSelector((state: IAllStates) => state.selectedCampaign, shallowEqual);

    useEffect(() => {
        getInfluencersData(
            campaignId,
            filterInfluencers,
            setListOfInfluencers,
            setTotalPages,
            setInfluencersInfoData as Dispatch<SetStateAction<iInfluencersData>>
        );
    }, [JSON.stringify(filterInfluencers), triggerUpdate]);

    const updateFeedbackLoop = () => {
        ManageService.updateFeedbackLoop(campaignId)
            .then(() => {
                setTriggerUpdate(prevState => !prevState);
                dispatch(selectedCampaignSlice.actions.setIsReviewed(false));
                SuccessToast(t('general.toasts.successDisableFeedback'));
            })
            .catch(error => ErrorToast(error));
    };

    const skipReporting = () => {
        ManageService.skipReporting(campaignId)
            .then(() => {
                setTriggerUpdate(prevState => !prevState);
                dispatch(selectedCampaignSlice.actions.setCanSkipReporting(true));
                SuccessToast(t('general.toasts.successSkipReporting'));
            })
            .catch(error => ErrorToast(error));
    };

    const handlePages = (updatePage: number) => {
        setFilterInfluencers({ ...filterInfluencers, page: updatePage });
        scrollToTop();
    };

    useEffect(() => {
        ManageService.getInfluencers(campaignId, { page: 1, perPage: 100 })
            .then(response => {
                setInfluencerDropdownList(response.data);
            })
            .catch(error => ErrorToast(error));
    }, []);

    const {
        platformDropdownList,
        filterParams,
        // influencerDropdownList,
        setFilterParams,
        setFilterModalInfluencerList,
        setFilterDropdownInfluencerList,
        // setFilterDropdownPlatforms,
        handleChangeParams,
    } = useCalendarFilters({ listOfInfluencers, campaignId });

    return isAclLoaded ? (
        <Row className="mb-5 mb-md-0 pb-5 pb-md-0">
            <div className="col-xl-9">
                <ManageCalendar
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    setFilterModalInfluencerList={setFilterModalInfluencerList}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    newEvent={newEvent}
                    setNewEvent={setNewEvent}
                    setTriggerUpdate={setTriggerUpdate}
                    campaignId={campaignId}
                    triggerUpdate={triggerUpdate}
                    influencerDropdownList={influencerDropdownList}
                    handleChangeParams={handleChangeParams}
                    setFilterDropdownInfluencerList={setFilterDropdownInfluencerList}
                />
                <div className="mt-1 mb-5 mb-md-0">
                    <div className="d-flex justify-content-md-end flex-wrap my-3">
                        {offerItemEventsEnum?.map(offerItem => {
                            const statusesWithIcon = ['proposed'];
                            return (
                                <React.Fragment key={offerItem}>
                                    {statusesWithIcon?.includes(offerItem) ? (
                                        <div className="mx-1 d-flex align-items-center">
                                            {offerItem === 'proposed' ? (
                                                <BsExclamationOctagon className="proposed-icon" />
                                            ) : (
                                                <BiRefresh className="proposed-icon text-dark" />
                                            )}
                                            <span className="ml-1">{getCorrectFilterTitle(offerItem, t)}</span>
                                        </div>
                                    ) : (
                                        <span
                                            key={offerItem}
                                            className={`calendar-button-dot mx-1
                              ${getCorrectColor(offerItem)}`}
                                        >
                                            {getCorrectFilterTitle(offerItem, t)}
                                        </span>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="col-xl-3 mt-4 mt-xl-0">
                <ManageCards
                    listOfInfluencers={listOfInfluencers}
                    influencersInfoData={influencersInfoData}
                    platformDropdownList={platformDropdownList}
                    filterParams={filterParams}
                    setFilterPlannerStatus={setFilterParams}
                    handleChangeParams={handleChangeParams}
                    setNewEvent={setNewEvent}
                    setShowModal={setShowModal}
                    updateFeedbackLoop={updateFeedbackLoop}
                    skipReporting={skipReporting}
                    influecersPagination={filterInfluencers}
                    handlePagination={handlePages}
                    totalPages={totalPages}
                    setTriggerUpdate={setTriggerUpdate}
                />
            </div>
        </Row>
    ) : null;
}
